import type { LocaleObject } from '@nuxtjs/i18n'
import type { GenericRecord } from '#api-types'

interface SeoEntity {
  extensions?: {
    novuSeoUrls?: GenericRecord
  }
}

/**
 * Composable to generate SEO meta links (canonical + hreflang alternates)
 * for products and categories with multilingual URLs
 *
 * @param reference - Ref or value containing entity with SEO URLs
 * @returns Computed ref with array of link objects
 */
export function useMetaLinks(reference: MaybeRef<SeoEntity | undefined>) {
  const runtimeConfig = useRuntimeConfig()
  const appUrl = runtimeConfig.public.APP_URL
  const route = useRoute()
  const targetPath = useTargetPath()
  const { locales, defaultLocale } = useI18n()

  return computed(() => {
    const entity = unref(reference)
    return generateMetaLinks(
      entity,
      appUrl,
      route.path,
      targetPath,
      locales.value,
      defaultLocale
    )
  })
}

/**
 * Generates canonical and alternate hreflang meta links for SEO
 * Following Google's best practices for multilingual sites
 *
 * @param entity - The entity with SEO URLs (product or category)
 * @param appUrl - Base application URL
 * @param routePath - Current route path
 * @param targetPath - Function to translate paths for different locales
 * @param locales - Available i18n locales
 * @param defaultLocale - Default locale code
 * @returns Array of link objects for useHead
 */
function generateMetaLinks(
  entity: SeoEntity | undefined,
  appUrl: string,
  routePath: string,
  targetPath: (path: string, locale?: string) => string,
  locales: LocaleObject[],
  defaultLocale: string
) {
  if (!entity?.extensions?.novuSeoUrls) {
    return []
  }

  const seoUrls = { ...entity.extensions.novuSeoUrls } as Record<string, string>
  delete seoUrls.apiAlias

  const defaultLocaleObj = locales.find((l) => l.code === defaultLocale)
  const xDefaultLangId = defaultLocaleObj?.languageId

  // Determine if this is a shop route (needs /shop prefix)
  const isShopRoute = routePath.includes('/shop/')

  // Generate alternate hreflang links for all available language versions
  const alternateLinks = Object.entries(seoUrls)
    .filter(([languageId]) => locales.some((l) => l.languageId === languageId))
    .flatMap(([languageId, path]) => {
      const matchingLocale = locales.find((l) => l.languageId === languageId)
      if (!matchingLocale) return []

      // Add /shop prefix if this is a shop route and path doesn't already have it
      const fullPath =
        isShopRoute && !path.startsWith('/shop/') ? `/shop/${path}` : path

      const translatedPath = targetPath(fullPath, matchingLocale.code)
      const href = `${appUrl}${translatedPath}`

      // Create language-specific hreflang link
      const languageLink = {
        rel: 'alternate',
        hreflang: matchingLocale.iso,
        href,
      }

      // Default locale gets additional x-default link for fallback
      if (languageId === xDefaultLangId) {
        return [languageLink, { rel: 'alternate', hreflang: 'x-default', href }]
      }

      return [languageLink]
    })

  // Canonical points to current page
  const canonicalLink = {
    rel: 'canonical',
    href: `${appUrl}${targetPath(routePath)}`,
  }

  return [canonicalLink, ...alternateLinks]
}
